import { useCallback, useMemo, useState } from 'react';
import { T, useGT, Var } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { Badge } from 'src/components/ui/badge';
import {
  CheckCircleIcon,
  XCircleIcon,
  SpinnerIcon,
} from '@phosphor-icons/react';
import { useTestStep } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';

interface InputSocket {
  id: string;
  name: string;
  type: string;
  required?: boolean;
  description?: string;
}

interface GenericStepTestSectionProps {
  stepType: string;
  config: Record<string, unknown>;
  inputSockets: InputSocket[];
}

interface TestResult {
  success: boolean;
  output?: Record<string, unknown>;
  error?: string;
  durationMs?: number;
}

/**
 * Per-input-socket form that calls the existing `workflows.testStep` RPC
 * with whatever the user types. Kept generic so the same component can
 * power the Test tab for any step whose handler can run in isolation —
 * `external_http_call`, plus future steps like `code_sandbox`.
 *
 * Skips JSON-only / record sockets when they're long; those get a
 * textarea where the user pastes JSON.
 */
export function GenericStepTestSection({
  stepType,
  config,
  inputSockets,
}: GenericStepTestSectionProps) {
  const gt = useGT();
  const [values, setValues] = useState<Record<string, string>>({});
  const [result, setResult] = useState<TestResult | null>(null);

  const testStepMutation = useTestStep();
  const isRunning = testStepMutation.isPending;

  const handleChange = useCallback((id: string, value: string) => {
    setValues((prev) => ({ ...prev, [id]: value }));
  }, []);

  const parseValueForSocket = useCallback(
    (socketType: string, raw: string): unknown => {
      const trimmed = raw.trim();
      if (trimmed === '') return undefined;
      if (socketType === 'number') {
        const n = Number(trimmed);
        return Number.isFinite(n) ? n : trimmed;
      }
      if (socketType === 'boolean') {
        return trimmed === 'true';
      }
      if (
        socketType === 'json' ||
        socketType.startsWith('array<') ||
        socketType === 'entity_reference' ||
        socketType.startsWith('entity_reference:')
      ) {
        try {
          return JSON.parse(trimmed);
        } catch {
          return trimmed;
        }
      }
      return trimmed;
    },
    [],
  );

  const handleRun = useCallback(async () => {
    setResult(null);
    const input: Record<string, unknown> = {};
    for (const socket of inputSockets) {
      const raw = values[socket.id] ?? '';
      const parsed = parseValueForSocket(socket.type, raw);
      if (parsed !== undefined) input[socket.id] = parsed;
    }
    try {
      const out = await testStepMutation.mutateAsync({
        stepType,
        input,
        config,
        timeoutMs: 60_000,
      });
      setResult(out);
    } catch (err) {
      setResult({
        success: false,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, [
    inputSockets,
    values,
    parseValueForSocket,
    testStepMutation,
    stepType,
    config,
  ]);

  const renderedFields = useMemo(
    () => inputSockets.filter((s) => !s.id.startsWith('__')),
    [inputSockets],
  );

  return (
    <div className="p-3 space-y-4">
      {renderedFields.length === 0 && (
        <p className="text-xs text-muted-foreground">
          <T context="external http call test tab no inputs">
            This step has no input sockets. Click Run to execute the handler
            with an empty input.
          </T>
        </p>
      )}

      <div className="space-y-3">
        {renderedFields.map((socket) => {
          const useTextarea =
            socket.type === 'json' ||
            socket.type.startsWith('array<') ||
            socket.type === 'entity_reference' ||
            socket.type.startsWith('entity_reference:');
          return (
            <div key={socket.id} className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">
                  {socket.name}
                  {socket.required && (
                    <span className="text-destructive ml-1">*</span>
                  )}
                </Label>
                <Badge
                  variant="outline"
                  className="text-[10px] font-mono uppercase"
                >
                  {socket.type}
                </Badge>
              </div>
              {useTextarea ? (
                <Textarea
                  rows={3}
                  className="font-mono text-xs"
                  placeholder={gt('Paste JSON (object or array)', {
                    context:
                      'generic workflow step test — placeholder for JSON socket textarea',
                  })}
                  value={values[socket.id] ?? ''}
                  onChange={(e) => handleChange(socket.id, e.target.value)}
                />
              ) : (
                <Input
                  type={socket.type === 'number' ? 'number' : 'text'}
                  value={values[socket.id] ?? ''}
                  onChange={(e) => handleChange(socket.id, e.target.value)}
                  placeholder={socket.description ?? socket.name}
                />
              )}
              {socket.description && (
                <p className="text-[11px] text-muted-foreground">
                  {socket.description}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <Button
        onClick={handleRun}
        disabled={isRunning}
        size="sm"
        className="w-full"
      >
        {isRunning ? (
          <>
            <SpinnerIcon className="h-4 w-4 mr-2 animate-spin" />
            <T context="step test run button running">Running…</T>
          </>
        ) : (
          <T context="step test run button">Run test</T>
        )}
      </Button>

      {result && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            {result.success ? (
              <CheckCircleIcon className="h-4 w-4 text-green-600" />
            ) : (
              <XCircleIcon className="h-4 w-4 text-destructive" />
            )}
            <span className="font-medium">
              {result.success ? (
                <T context="step test result success">Success</T>
              ) : (
                <T context="step test result failure">Failed</T>
              )}
            </span>
            {result.durationMs !== undefined && (
              <span className="text-xs text-muted-foreground">
                <T context="generic workflow step test — duration in ms">
                  <Var>{String(result.durationMs)}</Var>ms
                </T>
              </span>
            )}
          </div>
          {result.error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs">
              {result.error}
            </div>
          )}
          {result.output && (
            <div>
              <Label className="text-xs font-medium mb-1 block">
                <T context="step test output label">Output</T>
              </Label>
              <pre className="rounded-md border bg-muted/40 p-2 text-[11px] overflow-x-auto max-h-64">
                {JSON.stringify(result.output, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
